import type { MedicationDetails } from '../../../../lib/transferObjects/Medication';

export type IndicationItem = {
  indication?: string;
  description?: string;
};

export type OrderSetMedicationDetails = MedicationDetails & {
  medSectionBreak?: string;
};

export type MedicationDetailsWithIndications = MedicationDetails & {
  indications?: IndicationItem[];
};

function mapIndicationsToText(indications: IndicationItem[]): {
  indication?: string;
  indicationDescription?: string;
} {
  const first = indications.find(
    (item) => item?.indication || item?.description,
  );
  return {
    indication: first?.indication ?? undefined,
    indicationDescription: first?.description ?? undefined,
  };
}

export function mapToMedicationDetails(
  data: any,
): MedicationDetailsWithIndications {
  const details = data?.details ?? {};
  const indications: IndicationItem[] = Array.isArray(details?.indications)
    ? details.indications
    : Array.isArray(data?.indicationOptions)
      ? data.indicationOptions
      : Array.isArray(data?.indications)
        ? data.indications
        : [];
  const { indication, indicationDescription } =
    mapIndicationsToText(indications);

  const rawRegimen = details?.regimen ?? data?.regimen;

  return {
    medId: data.id ?? data.medId,
    medName: data.med_name ?? data.medName ?? details?.medName ?? undefined,
    brand: data.brand ?? details?.brand ?? undefined,
    subheading:
      data.subheading ??
      details?.subheading ??
      details?.subHeading ??
      undefined,
    furtherInformation:
      data.further_information ??
      data.furtherInformation ??
      details?.furtherInformation ??
      details?.further_information ??
      undefined,
    indications,
    indication,
    indicationDescription,
    isDeleted: Boolean(data.deleted_at),
    amt: {
      mpId: data.mp_id ?? data?.amt?.mpId ?? undefined,
      mpuuId: data.mpuu_id ?? data?.amt?.mpuuId ?? undefined,
      tpuuId: data.tpuu_id ?? data?.amt?.tpuuId ?? undefined,
    },
    regimen: rawRegimen
      ? {
          isDoseSchedule: rawRegimen.isDoseSchedule,
          isPrn: rawRegimen.isPrn,
          prn: rawRegimen.prn
            ? {
                prnNote: rawRegimen.prn.prnNote ?? undefined,
              }
            : undefined,
          isTaper: rawRegimen.isTaper,
          taper: rawRegimen?.taper ? { ...rawRegimen.taper } : {},
          doseSchedule: rawRegimen?.doseSchedule
            ? {
                isBreakfast: rawRegimen.doseSchedule.isBreakfast ?? false,
                breakfastNote: rawRegimen.doseSchedule.breakfastNote ?? '',
                isLunch: rawRegimen.doseSchedule.isLunch ?? false,
                lunchNote: rawRegimen.doseSchedule.lunchNote ?? '',
                isDinner: rawRegimen.doseSchedule.isDinner ?? false,
                dinnerNote: rawRegimen.doseSchedule.dinnerNote ?? '',
                isBed: rawRegimen.doseSchedule.isBed ?? false,
                bedNote: rawRegimen.doseSchedule.bedNote ?? '',
              }
            : undefined,
        }
      : undefined,
  };
}

export function mapToOrderSetMedicationDetails(
  data: any,
): OrderSetMedicationDetails {
  return {
    medId: data.medId,
    medName: data.details?.medName ?? '',
    brand: data.details?.brand ?? '',
    subheading: data.details?.subheading ?? '',
    furtherInformation: data.details?.furtherInformation ?? '',
    indication: data.details?.indication ?? '',
    indicationDescription: data?.details?.indicationDescription ?? '',
    medSectionBreak: data?.details?.medSectionBreak ?? '',
    medChangeType: data?.details?.medChangeType ?? '',
    furtherDescriptionOfChange: data?.details?.furtherDescriptionOfChange ?? '',
    isDeleted: Boolean(data.deletedAt),
    amt: {
      mpId: data.mp_id ?? undefined,
      mpuuId: data.mpuu_id ?? undefined,
      tpuuId: data.tpuu_id ?? undefined,
    },
    regimen: data.details?.regimen
      ? {
          isDoseSchedule: data?.details?.regimen?.isDoseSchedule ?? false,
          doseSchedule: data.details.regimen.doseSchedule
            ? {
                isBreakfast:
                  data.details.regimen.doseSchedule.isBreakfast ?? false,
                breakfastNote:
                  data.details.regimen.doseSchedule.breakfastNote ?? '',
                isLunch: data.details.regimen.doseSchedule.isLunch ?? false,
                lunchNote: data.details.regimen.doseSchedule.lunchNote ?? '',
                isDinner: data.details.regimen.doseSchedule.isDinner ?? false,
                dinnerNote: data.details.regimen.doseSchedule.dinnerNote ?? '',
                isBed: data.details.regimen.doseSchedule.isBed ?? false,
                bedNote: data.details.regimen.doseSchedule.bedNote ?? '',
              }
            : undefined,
          isPrn: data?.details?.regimen?.isPrn ?? false,
          prn: {
            prnNote: data?.details?.regimen?.prn?.prnNote ?? '',
          },
          isTaper: data?.details?.regimen?.isTaper ?? false,
          taper: data?.details.regimen?.taper
            ? {
                ...data?.details.regimen.taper,
              }
            : {},
        }
      : undefined,
  };
}
